<template>
  <div class="mb-10 flex size-full flex-col items-center md:max-w-7xl md:px-5 lg:px-10">
    <div class="mb-3 flex w-full items-center px-2 md:px-0">
      <p class="flex flex-col self-end text-xl text-gray-700 md:text-2xl lg:text-3xl">
        <span>Códigos de descuento</span>
        <span class="mt-1 text-sm font-light md:text-base"> Visualiza los códigos de descuento de tu marca </span>
      </p>
    </div>
    <div class="flex size-full flex-col rounded-md bg-white px-2 py-3 text-gray-700 md:px-4 md:py-5 lg:px-10">
      <div class="mb-5 flex w-full items-center self-start text-sm md:text-base">
        <template
          v-for="(value, key) in statusHash"
          :key="key"
        >
          <button
            class="shrink-0 border-b-2 py-3"
            :class="{ 'border-gray-300': selectedStatus !== key, 'border-lk-green': selectedStatus === key }"
            @click="updateSelectedStatus(key)"
          >
            {{ value }}
          </button>
          <div class="w-10 self-end border-b-2 border-gray-300 last:w-full" />
        </template>
      </div>
      <lokal-loading
        v-if="discountCodesIsLoading"
        :loading="discountCodesIsLoading"
      />
      <template v-else>
        <div
          v-if="paginationInfo"
          class="mb-4 text-sm text-gray-500 md:text-base"
        >
          {{ paginationInfo }}
        </div>
        <p
          v-if="!discountCodes || discountCodes.length === 0"
          class="text-sm md:text-base"
        >
          Aún no tienes códigos de descuento.
        </p>
        <div v-else>
          <makers-discount-codes-list-card
            v-for="discountCode in discountCodes"
            :key="discountCode.id"
            :discount-code="discountCode"
          />
          <pagination-component
            v-if="totalPages && totalPages > 1"
            :total-pages="totalPages"
            :current-page="currentPage"
            @set-new-page="handlePageChange"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import MakersDiscountCodesListCard from './makers-discount-codes-list-card.vue';
import PaginationComponent from './shared/pagination-component.vue';
import useDiscountCodesQuery from './queries/discount-codes-query';

const props = defineProps<{
  maker: Maker;
}>();

const STATUSES_HASH = {
  all: 'all',
  active: 'active',
  inactive: 'inactive',
} as const;

const PER_PAGE = 10;

const selectedStatus = ref<keyof typeof STATUSES_HASH>('all');
const statusHash = ref({ all: 'Todos', active: 'Activos', inactive: 'Inactivos' });
const currentPage = ref(1);

function updateSelectedStatus(status: keyof typeof STATUSES_HASH) {
  selectedStatus.value = status;
  currentPage.value = 1;
}

function handlePageChange(page: number) {
  currentPage.value = page;
}

const discountCodeQueryParams = computed(() => {
  const currentTimestamp = new Date().toISOString();
  if (selectedStatus.value === 'active') return `q[end_at_gt]=${currentTimestamp}`;
  if (selectedStatus.value === 'inactive') return `q[end_at_lt]=${currentTimestamp}`;
  return '';
});

const paginationInfo = computed(() => {
  if (!totalResources.value || totalResources.value === 0) {
    return null;
  }
  const startItem = (currentPage.value - 1) * resourcesPerPage.value + 1;
  const endItem = Math.min(currentPage.value * resourcesPerPage.value, totalResources.value);

  return `Mostrando ${startItem} - ${endItem} de ${totalResources.value} códigos de descuento`;
});

const { discountCodesIsLoading, discountCodes, totalResources, resourcesPerPage, totalPages } = useDiscountCodesQuery(
  computed(() => props.maker.id),
  discountCodeQueryParams,
  computed(() => currentPage.value),
  computed(() => PER_PAGE),
);

watch(
  totalPages,
  (newTotalPages) => {
    if (newTotalPages && currentPage.value > newTotalPages && newTotalPages > 0) {
      currentPage.value = newTotalPages;
    }
  },
  { immediate: true },
);
</script>
